import React, { lazy } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { getLanguage } from 'translation';
import Typography from '@mui/material/Typography';
import styled from '@emotion/styled';
import CofundedbytheEU from 'assets/icons/CofundedbytheEU.svg?react';

import Helmet from 'components/Helmet';
import RenderVideo from 'components/RawVideoRender';

const MainLayout = lazy(() => import('layouts/MainLayout'));
const Header = lazy(() => import('components/Header'));
const Footer = lazy(() => import('components/Footer'));
const About = lazy(() => import('pages/Home/components/About'));
const Services = lazy(() => import('pages/Home/components/Services'));
const Intro = lazy(() => import('pages/Home/components/Intro'));
const Media = lazy(() => import('pages/Home/components/Media'));
const GreenCards = lazy(() => import('./components/GreenCards'));

const ContentBackground = styled('div')(({ theme, grey }) => ({
  backgroundColor: theme.palette.backgroundNeutral.main
}));

const Content = styled('div')(({ theme }) => ({
  padding: '40px 100px',
  paddingBottom: 0,
  ...theme.wrapper,
  [theme.breakpoints.down('lg')]: {
    padding: 50
  },
  [theme.breakpoints.down('md')]: {
    padding: 30
  },
  [theme.breakpoints.down('sm')]: {
    padding: 20
  }
}));

const FlexContent = styled('div')(({ theme, flexDirection }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  ...theme.wrapper,
  maxWidth: 1030,
  gap: 15,
  [theme.breakpoints.down('md')]: {
    flexDirection: flexDirection || 'column'
  }
}));

const CoFoundWrapper = styled('div')(({ theme }) => ({
  padding: '80px 100px',
  ...theme.wrapper,
  [theme.breakpoints.down('lg')]: {
    padding: 50
  },
  [theme.breakpoints.down('md')]: {
    padding: 30
  },
  [theme.breakpoints.down('sm')]: {
    padding: 20
  }
}));

const CofundedbytheEUStyled = styled(CofundedbytheEU)(() => ({
  flex: 1
}));

const StyledCoFounderText = styled(Typography)(() => ({
  fontWeight: 600,
  fontSize: 18,
  flex: 2
}));

const AboutPage = ({ extended }) => {
  const t = useTranslate('HELMET');
  const tHome = useTranslate('Home');
  const [lang] = React.useState(() => getLanguage());

  const texts = React.useMemo(() => {
    const f = (k) => ({ [lang]: tHome(k) });
    return {
      hero_headline: f('HeroHeadline'),
      hero_subheadline: f('HeroSubheadline'),
      hero_button: f('HeroButton1'),
      about_field1: f('AboutField1'),
      about_field2: f('AboutField2'),
      about_field3: f('AboutField3'),
      value_headline: f('ValueHeadline'),
      value_subheadline: f('ValueSubheadline'),
      value_1_header: f('Value1Header'),
      value_1_text: f('Value1Text'),
      value_2_header: f('Value2Header'),
      value_2_text: f('Value2Text'),
      value_3_header: f('Value3Header'),
      value_3_text: f('Value3Text'),
      value_4_header: f('Value4Header'),
      value_4_text: f('Value4Text'),
      value_5_header: f('Value5Header'),
      value_5_text: f('Value5Text'),
      value_6_header: f('Value6Header'),
      value_6_text: f('Value6Text'),
      press_headline: f('PressHeadline'),
      press_subheadline: f('PressSubheadline'),
      funding_text: f('FundingText'),
      method_headline: f('MethodHeadline'),
      method_value_1_header: f('MethodValue1Header'),
      method_value_1_text: f('MethodValue1Text'),
      method_value_2_header: f('MethodValue2Header'),
      method_value_2_text: f('MethodValue2Text'),
      method_value_3_header: f('MethodValue3Header'),
      method_value_3_text: f('MethodValue3Text'),
      method_value_4_header: f('MethodValue4Header'),
      method_value_4_text: f('MethodValue4Text')
    };
  }, [tHome, lang]);

  return (
    <>
      <Helmet
        meta={{
          title: t('aboutUsTitle'),
          description: t('aboutUsDescription'),
          ogTitle: t('aboutUsOgTitle'),
          ogDescription: t('aboutUsOgDescription')
        }}
      />
      <MainLayout>
        {extended ? (
          <RenderVideo>
            <Header isHomePage={true} />
            <Intro texts={texts} />
          </RenderVideo>
        ) : (
          <Header />
        )}
        <ContentBackground>
          <Content>
            <About texts={texts} padding={'20px 0px 80px 0px'} />
          </Content>
        </ContentBackground>

        <Services texts={texts} />

        <GreenCards texts={texts} />

        <Media texts={texts} />

        <ContentBackground>
          <CoFoundWrapper>
            <FlexContent>
              <CofundedbytheEUStyled />
              <StyledCoFounderText>{texts?.funding_text?.[lang]}</StyledCoFounderText>
            </FlexContent>
          </CoFoundWrapper>
        </ContentBackground>

        <Footer />
      </MainLayout>
    </>
  );
};

export default AboutPage;
